import styled from '@emotion/styled'
import { useMachine } from '@xstate/react'
import {
  ApiBusinessUnit,
  ApiDfm,
  ApiRebalancingFrequency,
  ApiRebalancingType,
  ApiSavedPortfolio,
  ApiStrategyTemplate
} from 'api/rtk-api'
import { useEffect } from 'react'

import { formatToDateDisplay } from '@/utilities/date'

import {
  Attribute,
  DurationButtons,
  EditableAssetTable,
  FeesInput,
  FormItem,
  Label,
  MessageBox,
  Section,
  Select,
  Space,
  StrategySelector,
  TextInput,
  Typography,
  ValidationMessage
} from '../../../components/core-v2'
import Icon from '../../../components/core-v2/atoms/Icon'
import IconButton from '../../../components/core-v2/molecules/iconButton'
import LinkIcon from '../../../components/core-v2/molecules/linkIcon'
import { Asset } from '../../../components/core-v2/organisms/asset-table-editable'
import { TENANT } from '../../../const'
import { availableTolerances, toleranceToPercentageString } from '../../../utilities'
import { AssetSummaryItem } from '../../tradebuilder-v2/modules/03-portfolio/types'
import { Errors, round } from '../../tradebuilder-v2/util'
import { ClientsMachine } from '../../user-admin/clients/clients.state'
import { DfmsMachine } from '../../user-admin/clients/dfm.state'
import { usePortfolioPermissions } from '../hooks'
import { EditPortfoliosActions } from '../state/edit-portfolio-slice'
import { useAppSelector } from '../store/hooks'

export type PortfolioDetailsProps = {
  actions: EditPortfoliosActions
  areDisallowedStrategiesOverridden: boolean
  assetPerformances: AssetSummaryItem[]
  availableCptyIds: string[]
  availableStrategies: ApiStrategyTemplate[]
  errors: Errors
  isFundOfFund: boolean
  isNew: boolean
  portfolio: ApiSavedPortfolio
  readonlyAccess: boolean
  selectedAssetDuration: string
  totalWeight: number
}

type Owner = {
  id: string | null
  name: string
}

const StyledAddNewPortfolio = styled.section`
  input[type='radio'] {
    margin-right: 8px;
  }
  input:read-only {
    background: #fafafa;
    color: var(--White40);
    border: solid 1px var(--White90);
    cursor: default;
  }
`

const availableFeeBases = ['TotalTradeValue', 'RiskyAssetExposure', 'LoanAmount']
const availableFeeFreqs = ['Quarterly', 'Monthly', 'Annually']
const availableFeeTypes = ['PortfolioManagementFee', 'DiscretionaryFundManagementFee']

const AssetFinderButton = ({ onClick }: { onClick: () => void }) => (
  <Section type="primary">
    <IconButton
      disabled={false}
      icon={<Icon name="circleAdd" size="default" />}
      iconPosition="left"
      operationType="progress"
      text="Add an asset"
      width="100%"
      onClick={onClick}
    />
  </Section>
)

const mandatoryFieldName = (label: string, readonlyAccess: boolean) =>
  readonlyAccess ? label : `${label} *`

export const PortfolioDetailsTab = ({
  actions,
  areDisallowedStrategiesOverridden,
  assetPerformances,
  availableCptyIds,
  availableStrategies,
  errors,
  isFundOfFund,
  isNew,
  portfolio,
  readonlyAccess,
  selectedAssetDuration,
  totalWeight
}: PortfolioDetailsProps) => {
  const state = useAppSelector(state => state.editPortfolios)
  const { isUserPortfolioRestricted } = usePortfolioPermissions(state)

  type Selected = 'yes' | 'no' | 'unavailable'
  type Strategy = {
    id: string
    name: string
    selected: Selected
  }
  const strategyList = availableStrategies.map(
    x =>
      ({
        id: x.strategyTemplateId,
        name: x.strategyName,
        selected: portfolio.disallowedStrategies.includes(x.strategyTemplateId)
          ? ('no' as Selected)
          : ('yes' as Selected)
      } as Strategy)
  )

  const assetPerfs = assetPerformances
  const assets: Asset[] = portfolio.targetWeights.map(
    asset =>
      ({
        id: asset.asset.assetId,
        name: asset.asset.name,
        return:
          assetPerfs.length === 0
            ? null
            : assetPerfs.find(x => x.id === asset.asset.assetId)?.return ?? null,
        volatility:
          assetPerfs.length === 0
            ? null
            : assetPerfs.find(x => x.id === asset.asset.assetId)?.volatility ?? null,
        weight: asset.weight,
        investmentWeight:
          'investmentWeights' in portfolio
            ? (portfolio as any).investmentWeights.find(x => x.assetId === asset.asset.assetId)
                ?.weight
            : null
      } as Asset)
  )

  useEffect(() => {
    const setDefaultWeight = () => {
      if (
        portfolio?.rebalancingDefinition?.type === 'Weight' &&
        portfolio?.rebalancingDefinition?.weightsTolerance === undefined
      ) {
        actions.setRebalTolerance(Number(availableTolerances[0]))
      }
    }
    setDefaultWeight()

    const setDefaultFrequency = () => {
      if (
        (portfolio?.rebalancingDefinition?.type === 'Frequency' ||
          portfolio?.rebalancingDefinition?.type === 'CalendarFrequency') &&
        portfolio?.rebalancingDefinition?.frequency === undefined
      ) {
        actions.setRebalFreq('Monthly')
      }
    }
    setDefaultFrequency()
  }, [actions, portfolio?.rebalancingDefinition])
  const [currentClients, sendClients] = useMachine(ClientsMachine)
  const [currentDfms, sendDfms] = useMachine(DfmsMachine)

  useEffect(() => {
    sendDfms({ type: 'FETCH_DFMS', tenant: TENANT })
    sendClients({ type: 'FETCH_CLIENTS', tenant: TENANT })
  })

  const dfms = [...(currentDfms.context.dfms || [])]
  const bUs = [...(currentClients.context.clients || [])].reduce<ApiBusinessUnit[]>(
    (bUs, client) => [...bUs, ...client.businessUnits],
    []
  )

  const owners: Owner[] = [...dfms, ...bUs].reduce<Owner[]>(
    (acc, owner) => {
      const name = (owner as ApiBusinessUnit).businessUnitName || (owner as ApiDfm).dfmName
      const id = (owner as ApiBusinessUnit).businessUnitId || (owner as ApiDfm).dfmId
      return [...acc, { id, name }]
    },
    [{ id: null, name: 'None' }]
  )

  const ownerId = (portfolio as any).portfolioOwnerId

  return (
    <StyledAddNewPortfolio>
      <Space pt={6}>
        <FormItem mb={6}>
          <Label>{mandatoryFieldName('Name', readonlyAccess)}</Label>
          <TextInput
            readOnly={readonlyAccess}
            value={portfolio.portfolioName}
            onChange={v => actions.setPortfolioName(v.target.value)}
          />
          <ValidationMessage errors={errors} fieldName="portfolio_name" />
        </FormItem>
        {!isUserPortfolioRestricted && (
          <Space ismax mr={3}>
            <FormItem mb={6}>
              <Label>Owner</Label>
              <Select
                value={ownerId}
                onChange={v =>
                  actions.setPortfolioOwnerId(owners.find(x => x.id === v.target.value).id)
                }
              >
                {owners.map(owner => (
                  <option key={owner?.name} value={owner?.id}>
                    {owner?.name}
                  </option>
                ))}
              </Select>
            </FormItem>
          </Space>
        )}
        {!isNew && (
          <FormItem mb={6}>
            <Label>Portfolio Id</Label>
            <TextInput
              readOnly
              value={portfolio.portfolioId}
              onChange={v => actions.setPortfolioId(v.target.value)}
            />
            <ValidationMessage errors={errors} fieldName="id" />
          </FormItem>
        )}
        <Section mb={6} type="tertiary">
          <FormItem>
            <Section px={5} py={6} type="quaternary">
              <Space mb={3}>
                <Typography fontSize="13px" variant="b3">
                  Assets
                </Typography>
              </Space>
              <Typography fontSize="13px" variant="b3">
                Choose the assets to be used in the portfolio
              </Typography>
            </Section>
            <Space mx={5} my={6}>
              <DurationButtons
                items={['6m', '1y', '3y', '5y', '10y']}
                readonly={readonlyAccess}
                selectedItem={selectedAssetDuration}
                onSelect={item => actions.setSelectedAssetDuration(item)}
              />
              <Section mt={6} type="nesting-only">
                <EditableAssetTable
                  assets={assets}
                  isFundOfFund={isFundOfFund}
                  isNew={isNew}
                  readonly={readonlyAccess}
                  onChangeAssetWeight={(assetId, weight) => {
                    actions.changeAssetWeight({ assetId, weight })
                  }}
                  onClickAsset={assetId => actions.inspectAsset(assetId)}
                  onRemoveAsset={assetId => actions.removeAsset(assetId)}
                />
              </Section>
              {!readonlyAccess && (
                <>
                  <Space isflex alignItems="center" justifyContent="space-between" mt={6}>
                    <LinkIcon
                      disabled={false}
                      icon={<Icon name="equalise" size="default" />}
                      iconPosition="left"
                      text="Equalise weights"
                      onClick={() => actions.equaliseAssetWeights()}
                    />
                    <LinkIcon
                      disabled={false}
                      icon={<Icon name="scales" size="default" />}
                      iconPosition="left"
                      text="Scale weights to 100%"
                      onClick={() => actions.scaleAssetWeights()}
                    />
                    <Typography fontSize="15px" fontWeight="800" variant="b3">
                      Total: {round(totalWeight * 100, 2)}%
                    </Typography>
                  </Space>
                  <ValidationMessage errors={errors} fieldName="total_weights" />
                  <Space pt={6}>
                    <AssetFinderButton onClick={() => actions.chooseAsset()} />
                  </Space>
                </>
              )}
            </Space>
          </FormItem>
        </Section>
        <FormItem mb={6}>
          <Label>Portfolio multiplier</Label>
          <TextInput
            readOnly={readonlyAccess}
            value={portfolio.multiplier === 0 ? '' : portfolio.multiplier}
            onChange={v => actions.setMultiplier(Number(v.target.value))}
          />
        </FormItem>

        <Space isflex alignItems="center">
          <Space ismax mr={3}>
            <FormItem mb={6}>
              <Label>{mandatoryFieldName('Rebalancing', readonlyAccess)}</Label>
              <Select
                disabled={readonlyAccess}
                value={portfolio.rebalancingDefinition.type}
                onChange={v => actions.setRebalType(v.target.value as ApiRebalancingType)}
              >
                <option value="None">None</option>
                <option value="Weight">Weight</option>
                <option value="Frequency">Frequency</option>
                <option value="CalendarFrequency">Calendar Frequency</option>
              </Select>
            </FormItem>
          </Space>
          <Space ismax ml={3}>
            {portfolio?.rebalancingDefinition?.type === 'Weight' && (
              <FormItem mb={6}>
                <Label>Weight</Label>
                <Select
                  disabled={readonlyAccess}
                  value={portfolio?.rebalancingDefinition?.weightsTolerance}
                  onChange={v => actions.setRebalTolerance(Number(v.target.value))}
                >
                  {availableTolerances.map(c => (
                    <option key={c} value={c}>
                      {toleranceToPercentageString(c)}
                    </option>
                  ))}
                </Select>
              </FormItem>
            )}
            {(portfolio?.rebalancingDefinition?.type === 'Frequency' ||
              portfolio?.rebalancingDefinition?.type === 'CalendarFrequency') && (
              <FormItem mb={6}>
                <Label>Frequency</Label>
                <Select
                  disabled={readonlyAccess}
                  value={portfolio?.rebalancingDefinition?.frequency}
                  onChange={v => actions.setRebalFreq(v.target.value as ApiRebalancingFrequency)}
                >
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="SemiAnnually">Semi annually</option>
                  <option value="Annually">Annually</option>
                </Select>
              </FormItem>
            )}
          </Space>
        </Space>
        <Section mb={5} px={5} py={6} type="secondary">
          <Section type="primary">
            <Space isflex flexDirection="column">
              <Attribute name="Portfolio ID">{portfolio.portfolioId}</Attribute>
            </Space>
            <Space isflex flexDirection="column">
              <Attribute name="Tax classification">{portfolio.taxClassification}</Attribute>
            </Space>
            {'startDate' in portfolio && (
              <Space isflex flexDirection="column">
                <Attribute name="Start date">
                  {'startDate' in portfolio && formatToDateDisplay((portfolio as any).startDate)}
                </Attribute>
              </Space>
            )}
          </Section>
        </Section>
        <Section mb={6} type="tertiary">
          <FormItem>
            <Section px={5} py={6} type="quaternary">
              <Space mb={3}>
                <Typography fontSize="13px" variant="b3">
                  Fees
                </Typography>
              </Space>
              <Typography fontSize="13px" variant="b3">
                Choose the fees that will apply to this portfolio
              </Typography>
            </Section>
            <Section pt={6} px={5} type="nesting-only">
              <FeesInput
                availableCptyIds={availableCptyIds}
                availableFeeBases={availableFeeBases}
                availableFeeFreqs={availableFeeFreqs}
                availableFeeTypes={availableFeeTypes}
                displayFees={portfolio.fees}
                feeCategory="portfolio"
                readonly={readonlyAccess}
                title="Portfolio charge"
                onAddNewFee={fee => actions.addNewFee(fee)}
                onFeePropertyChange={(index, property, updatedValue) =>
                  actions.changeFeeProperty({ index, property, updatedValue })
                }
                onRemoveExistingFee={(index: number) => actions.removeFee(index)}
              />
            </Section>
            <ValidationMessage errors={errors} fieldName="feeRates" />
          </FormItem>
        </Section>
        {!isUserPortfolioRestricted && (
          <Section mb={6} type="tertiary">
            <FormItem>
              <Section px={5} py={6} type="quaternary">
                <Space mb={3}>
                  <Typography fontSize="13px" variant="b3">
                    Strategies
                  </Typography>
                </Space>
                <Typography fontSize="13px" variant="b3">
                  Choose the strategies that this portfolio can be used with
                </Typography>
              </Section>
              <Section pb={0} pt={5} px={5} type="nesting-only">
                <StrategySelector
                  readonly={readonlyAccess}
                  strategies={strategyList}
                  onChange={(id, val) => actions.setStrategyPermission({ id, val })}
                />
              </Section>
              {!readonlyAccess && (
                <Section pb={0} pt={5} px={5} type="nesting-only">
                  {areDisallowedStrategiesOverridden && (
                    <MessageBox
                      title="Strategy permissions have been overridden and are currently different to those
                implied by the asset composition"
                    >
                      <LinkIcon
                        disabled={false}
                        icon={<Icon name="equalise" size="default" />}
                        iconPosition="left"
                        text="Reset allowed strategies based on asset permissions"
                        onClick={() => actions.resetDisallowedStrategies()}
                      />
                    </MessageBox>
                  )}
                </Section>
              )}
            </FormItem>
          </Section>
        )}
      </Space>
    </StyledAddNewPortfolio>
  )
}
